import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Home, Users, Trophy, User, Menu, X, MapPin } from 'lucide-react';
import Dashboard from './components/Dashboard';
import Leaderboard from './components/Leaderboard';
import Scoring from './components/Scoring';
import TournamentScoring from './components/TournamentScoring';
import AdminLanding from './components/AdminLanding';
import AdminUsers from './components/AdminUsers';
import AdminClubs from './components/AdminClubs';
import AdminEngagement from './components/AdminEngagement';
import AdminFeatureTesting from './components/AdminFeatureTesting';
import TournamentManagement from './components/TournamentManagement';
import Profile from './components/Profile';
import BetaProfile from './components/BetaProfile';
import SimulatorCourses from './components/SimulatorCourses';
import UserTrackingPage from './components/UserTrackingPage';
import AdminUserProfile from './components/AdminUserProfile';
import AvailableTournaments from './components/AvailableTournaments';
import NewWeeklyScoring from './components/NewWeeklyScoring';
import NewWeeklyLeaderboard from './components/NewWeeklyLeaderboard';
import AdminPermissionsManager from './components/AdminPermissionsManager';
import LeaguesLanding from './components/LeaguesLanding';
import LeagueAdminPanel from './components/LeagueAdminPanel';
import AllLeagues from './components/AllLeagues';
import CaptainDashboard from './components/CaptainDashboard';
import LeagueScoring from './components/LeagueScoring';
import PlayerAvailabilityCalendar from './components/PlayerAvailabilityCalendar';
import PlayerAvailabilityForm from './components/PlayerAvailabilityForm';
import PlayerTeamView from './components/PlayerTeamView';
import LeagueStandings from './components/LeagueStandings';
import DivisionStandings from './components/DivisionStandings';
import TeamDetailsPage from './components/TeamDetailsPage';
import WeeklyResults from './components/WeeklyResults';
import { AuthProvider, useAuth } from './AuthContext';
import { usePermissions } from './hooks/usePermissions';
import ClubProDashboard from './components/ClubProDashboard';
import Login from './components/Login';
import PasswordSetup from './components/PasswordSetup';
import ClaimAccount from './components/ClaimAccount';
import ResetPassword from './components/ResetPassword';
import BookingPage from './components/BookingPage';
import BookingSettingsAdmin from './components/BookingSettingsAdmin';
import ClubManagement from './components/ClubManagement';
import BookingGuard from './components/BookingGuard';
import OnboardingWorkflow from './components/OnboardingWorkflow';
import ViewAsModeIndicator from './components/ViewAsModeIndicator';
import WeeklyChallengeCard from './components/WeeklyChallengeCard';
import ChallengesList from './components/ChallengesList';
import ChallengeLeaderboard from './components/ChallengeLeaderboard';
import WeeklyChallengeAdmin from './components/WeeklyChallengeAdmin';
import ComponentShowcase from './components/ComponentShowcase';
import SignupManager from './components/SignupManager';
import SignupDetails from './components/SignupDetails';
import SignupList from './components/SignupList';
import SignupRegistration from './components/SignupRegistration';
import RegistrationTemplateManager from './components/RegistrationTemplateManager';
import { SimulatorBuilder } from './components/SimulatorBuilder';
import ComingSoon from './components/ComingSoon';
import AdminShotCapture from './components/AdminShotCapture';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Wrapper components to handle URL parameters
const NewWeeklyScoringWrapper: React.FC = () => {
  const { tournamentId } = useParams();
  return (
    <NewWeeklyScoring 
      tournamentId={parseInt(tournamentId || '1')} 
      tournamentName="Weekly Tournament" 
    />
  );
};

const NewWeeklyLeaderboardWrapper: React.FC = () => {
  const { tournamentId } = useParams();
  return (
    <NewWeeklyLeaderboard 
      tournamentId={parseInt(tournamentId || '1')} 
      tournamentName="Weekly Tournament" 
    />
  );
};

// Wrapper for LeagueStandings to handle URL parameters
const LeagueStandingsWrapper: React.FC = () => {
  const { leagueId } = useParams();
  return <LeagueStandings leagueId={parseInt(leagueId || '1')} />;
};

// Wrapper for DivisionStandings to handle URL parameters
const DivisionStandingsWrapper: React.FC = () => {
  const { leagueId, divisionId } = useParams();
  return (
    <DivisionStandings 
      leagueId={parseInt(leagueId || '1')} 
      divisionId={parseInt(divisionId || '1')} 
    />
  );
};

// Wrapper for TeamDetailsPage to handle URL parameters
const TeamDetailsWrapper: React.FC = () => {
  const { teamId, leagueId } = useParams();
  return (
    <TeamDetailsPage
      teamId={parseInt(teamId || '1')}
      leagueId={leagueId ? parseInt(leagueId) : undefined}
    />
  );
};

// Wrapper for CaptainDashboard to handle URL parameters
const CaptainDashboardWrapper: React.FC = () => {
  const { teamId, leagueId } = useParams();
  return (
    <CaptainDashboard
      teamId={parseInt(teamId || '1')}
      leagueId={parseInt(leagueId || '1')}
    />
  );
};

// Wrapper for PlayerAvailabilityCalendar to handle URL parameters
const PlayerAvailabilityCalendarWrapper: React.FC = () => {
  const { teamId, leagueId } = useParams();
  return (
    <PlayerAvailabilityCalendar
      teamId={parseInt(teamId || '1')}
      leagueId={parseInt(leagueId || '1')}
    />
  );
};

// Wrapper for PlayerTeamView to handle URL parameters
const PlayerTeamViewWrapper: React.FC = () => {
  const { teamId, leagueId } = useParams();
  return (
    <PlayerTeamView
      teamId={parseInt(teamId || '1')}
      leagueId={parseInt(leagueId || '1')}
    />
  );
};

// Wrapper for WeeklyResults to handle URL parameters
const WeeklyResultsWrapper: React.FC = () => {
  const { leagueId, weekNumber } = useParams();
  return (
    <WeeklyResults 
      leagueId={parseInt(leagueId || '1')} 
      weekNumber={weekNumber ? parseInt(weekNumber) : undefined} 
    />
  );
};

// Wrapper for AvailableTournaments to handle query parameters
const AvailableTournamentsWrapper: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tournamentId = searchParams.get('tournament');

  console.log('AvailableTournamentsWrapper: tournamentId from query params:', tournamentId);

  return <AvailableTournaments />;
};

// Wrapper for ChallengeLeaderboard to handle URL parameters
const ChallengeLeaderboardWrapper: React.FC = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  return (
    <ChallengeLeaderboard
      challengeId={parseInt(challengeId || '0')}
      onBack={() => navigate('/challenges')}
    />
  );
};

// Wrapper for WeeklyChallengeCard to handle navigation
const WeeklyChallengeCardWrapper: React.FC = () => {
  const navigate = useNavigate();
  return (
    <WeeklyChallengeCard
      onViewLeaderboard={(challengeId) => navigate(`/challenges/${challengeId}`)}
    />
  );
};

// Wrapper for Challenges page
const ChallengesPageWrapper: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Trophy className="w-8 h-8 text-yellow-500" />
          CTP Challenges
        </h1>
      </div>
      <ChallengesList />
    </div>
  );
};

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  console.log('ProtectedRoute: loading=', loading, 'user=', user ? 'exists' : 'none', 'pathname=', location.pathname);
  
  useEffect(() => {
    if (!loading && !user) {
      console.log('ProtectedRoute: Redirecting to login from', location.pathname);
      navigate('/login', { state: { from: location.pathname + location.search } });
    }
  }, [loading, user, navigate, location]);
  
  if (loading) {
    console.log('ProtectedRoute: Showing loading state');
    return <div className="p-4 text-center">Loading...</div>;
  }
  
  if (!user) {
    console.log('ProtectedRoute: No user, showing loading while redirecting');
    return <div className="p-4 text-center">Redirecting to login...</div>;
  }
  
  console.log('ProtectedRoute: Rendering protected content for', location.pathname);
  return <>{children}</>;
}

function AdminProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const hasAccess = hasPermission('access_admin_panel');

  console.log('AdminProtectedRoute: loading=', loading, 'user=', user ? 'exists' : 'none', 'hasAccess=', hasAccess, 'pathname=', location.pathname);

  useEffect(() => {
    if (!loading) {
      if (!user) {
        console.log('AdminProtectedRoute: No user, redirecting to login');
        navigate('/login');
      } else if (!hasAccess) {
        console.log('AdminProtectedRoute: User lacks access_admin_panel permission, redirecting to home');
        navigate('/');
      }
    }
  }, [loading, user, navigate, hasAccess]);

  if (loading) {
    console.log('AdminProtectedRoute: Showing loading state');
    return <div className="p-4 text-center">Loading...</div>;
  }

  if (!user) {
    console.log('AdminProtectedRoute: No user, showing loading while redirecting');
    return <div className="p-4 text-center">Redirecting to login...</div>;
  }

  if (!hasAccess) {
    console.log('AdminProtectedRoute: User lacks permission, showing loading while redirecting');
    return <div className="p-4 text-center">Redirecting...</div>;
  }

  console.log('AdminProtectedRoute: Rendering admin content for', location.pathname);
  return <>{children}</>;
}

function ClubProProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const hasAccess = hasAnyPermission(['view_club_handicaps', 'manage_club_members']);

  console.log('ClubProProtectedRoute: loading=', loading, 'user=', user ? 'exists' : 'none', 'hasAccess=', hasAccess, 'pathname=', location.pathname);

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/login');
      } else if (!hasAccess) {
        navigate('/');
      }
    }
  }, [loading, user, navigate, hasAccess]);

  if (loading) {
    return <div className="p-4 text-center">Loading...</div>;
  }

  if (!user) {
    return <div className="p-4 text-center">Redirecting to login...</div>;
  }

  if (!hasAccess) {
    return <div className="p-4 text-center">Redirecting...</div>;
  }

  return <>{children}</>;
}

function BetaProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // Only beta testers can access - admins need explicit beta permission for demo
  const hasAccess = hasPermission('access_beta_features');

  console.log('BetaProtectedRoute: loading=', loading, 'user=', user ? 'exists' : 'none', 'hasAccess=', hasAccess, 'pathname=', location.pathname);

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/login', { state: { from: location.pathname + location.search } });
      } else if (!hasAccess) {
        navigate('/');
      }
    }
  }, [loading, user, navigate, hasAccess, location]);

  if (loading) {
    return <div className="p-4 text-center">Loading...</div>;
  }

  if (!user) {
    return <div className="p-4 text-center">Redirecting to login...</div>;
  }

  if (!hasAccess) {
    return <div className="p-4 text-center">Access restricted to beta testers...</div>;
  }

  return <>{children}</>;
}

function SignupsProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  console.log('SignupsProtectedRoute: loading=', loading, 'user=', user ? 'exists' : 'none', 'pathname=', location.pathname);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: location.pathname + location.search } });
    }
  }, [loading, user, navigate, location]);

  if (loading) {
    return <div className="p-4 text-center">Loading...</div>;
  }

  if (!user) {
    return <div className="p-4 text-center">Redirecting to login...</div>;
  }

  return <>{children}</>;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  console.log('HomeRoute: loading=', loading, 'user=', user ? 'exists' : 'none', 'pathname=', location.pathname);
  
  useEffect(() => {
    if (!loading && user) {
      console.log('HomeRoute: User is logged in, redirecting to profile');
      navigate('/profile', { replace: true });
    }
  }, [loading, user, navigate]);
  
  if (loading) {
    console.log('HomeRoute: Showing loading state');
    return <div className="p-4 text-center">Loading...</div>;
  }
  
  if (user) {
    console.log('HomeRoute: User is logged in, showing loading while redirecting');
    return <div className="p-4 text-center">Redirecting to profile...</div>;
  }
  
  console.log('HomeRoute: Rendering dashboard for non-authenticated user');
  return <Dashboard />;
}

function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  console.log('Navigation: user=', user ? 'exists' : 'none', 'current pathname=', location.pathname);

  const navigationItems = [
    ...(!user ? [{ to: "/", icon: Home, label: "Home" }] : []),
    { to: "/signups", icon: Trophy, label: "Compete" },
    { to: "/simulator-courses", icon: MapPin, label: "Neighborhood Courses" },
    { to: "https://neighborhood-national.mn.co/", icon: Users, label: "Community", external: true },
    ...(user ? [
      { to: "/profile", icon: User, label: "Profile" }
    ] : []),
  ];

  return (
    <nav className="bg-white/95 backdrop-blur-sm shadow-lg relative z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img src={process.env.PUBLIC_URL + "/Logo_N_Dark.png"} alt="NN Logo" className="h-8 w-auto sm:h-10" />
              </Link>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navigationItems.map((item) => (
              item.external ? (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-brand-black hover:bg-brand-neon-green hover:text-brand-black transition-colors"
                >
                  <item.icon className="w-5 h-5 mr-2" />
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-brand-black hover:bg-brand-neon-green hover:text-brand-black transition-colors"
                >
                  <item.icon className="w-5 h-5 mr-2" />
                  {item.label}
                </Link>
              )
            ))}
            
            {/* Auth Buttons */}
            <div className="ml-4 flex items-center space-x-2">
              {user ? (
                <>
                  <button
                    onClick={logout}
                    className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-brand-black hover:bg-brand-neon-green hover:text-brand-black transition-colors"
                  >
                    Login
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-3 rounded-md text-brand-black hover:bg-brand-neon-green hover:text-brand-black transition-colors focus:outline-none focus:ring-2 focus:ring-brand-neon-green focus:ring-offset-2"
              aria-expanded="false"
              aria-label="Toggle navigation menu"
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div className={`md:hidden ${mobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="px-4 pt-2 pb-4 space-y-2 bg-white/95 backdrop-blur-sm border-t border-gray-200">
          {navigationItems.map((item) => (
            item.external ? (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-4 py-3 rounded-lg text-base font-medium text-brand-black hover:bg-brand-neon-green hover:text-brand-black transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon className="w-6 h-6 mr-3" />
                {item.label}
              </a>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center px-4 py-3 rounded-lg text-base font-medium text-brand-black hover:bg-brand-neon-green hover:text-brand-black transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon className="w-6 h-6 mr-3" />
                {item.label}
              </Link>
            )
          ))}
          
          {/* Mobile Auth Buttons */}
          <div className="border-t border-gray-200 pt-3 mt-3">
            {user ? (
              <>
                <button
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="flex items-center px-4 py-3 rounded-lg text-base font-medium text-brand-black hover:bg-brand-neon-green hover:text-brand-black transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

function AppContent() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-dark-green to-brand-muted-green">
      {/* Global View-As Mode Indicator */}
      <ViewAsModeIndicator />
      
      {/* Navigation */}
      <Navigation />

      {/* Routes with different layouts */}
      <Routes>
        {/* Full-width routes */}
        <Route path="/weekly-scoring/:tournamentId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute>
              <NewWeeklyScoringWrapper />
            </ProtectedRoute>
          </main>
        } />
        <Route path="/weekly-leaderboard/:tournamentId" element={
          <NewWeeklyLeaderboardWrapper />
        } />
        
        {/* Standard container routes */}
        <Route path="/" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <HomeRoute />
          </main>
        } />
        <Route path="/leaderboard" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <Leaderboard />
          </main>
        } />
        <Route path="/leaderboard/tournament/:tournamentId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <Leaderboard />
          </main>
        } />
        <Route path="/tournaments" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><AvailableTournamentsWrapper /></ProtectedRoute>
          </main>
        } />
        <Route path="/club-pro" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ClubProProtectedRoute><ClubProDashboard /></ClubProProtectedRoute>
          </main>
        } />
        <Route path="/scoring" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <Scoring />
          </main>
        } />
        <Route path="/tournament-scoring" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><TournamentScoring /></ProtectedRoute>
          </main>
        } />
        <Route path="/simulator-courses" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <SimulatorCourses />
          </main>
        } />
        <Route path="/simulator-builder" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <SimulatorBuilder />
          </main>
        } />
        <Route path="/profile" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><Profile /></ProtectedRoute>
          </main>
        } />
        <Route path="/beta-profile" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <BetaProtectedRoute><BetaProfile /></BetaProtectedRoute>
          </main>
        } />
        <Route path="/admin" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><AdminLanding /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/users" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/clubs" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><AdminClubs /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/engagement" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><AdminEngagement /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/shot-capture" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><AdminShotCapture /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/feature-testing" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><AdminFeatureTesting /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/club-management" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><ClubManagement /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/tournament-management" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><TournamentManagement /></ProtectedRoute>
          </main>
        } />
        <Route path="/user-tracking" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><UserTrackingPage /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/users/:userId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><AdminUserProfile /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/permissions" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><AdminPermissionsManager /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/league-management" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><LeaguesLanding /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/league/:leagueId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><LeagueAdminPanel /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/captain-dashboard/:teamId/:leagueId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><CaptainDashboardWrapper /></ProtectedRoute>
          </main>
        } />
        <Route path="/league-scoring" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><LeagueScoring /></ProtectedRoute>
          </main>
        } />
        <Route path="/league-scoring/:matchupId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><LeagueScoring /></ProtectedRoute>
          </main>
        } />
        <Route path="/player/availability/:teamId/:leagueId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><PlayerAvailabilityCalendarWrapper /></ProtectedRoute>
          </main>
        } />
        <Route path="/player/availability-form" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><PlayerAvailabilityForm /></ProtectedRoute>
          </main>
        } />
        <Route path="/player/team/:teamId/:leagueId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><PlayerTeamViewWrapper /></ProtectedRoute>
          </main>
        } />
        <Route path="/leagues" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AllLeagues />
          </main>
        } />
        <Route path="/league-standings/:leagueId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <LeagueStandingsWrapper />
          </main>
        } />
        <Route path="/league-standings/:leagueId/division/:divisionId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <DivisionStandingsWrapper />
          </main>
        } />
        <Route path="/team/:teamId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <TeamDetailsWrapper />
          </main>
        } />
        <Route path="/team/:teamId/league/:leagueId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <TeamDetailsWrapper />
          </main>
        } />
        <Route path="/weekly-results/:leagueId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <WeeklyResultsWrapper />
          </main>
        } />
        <Route path="/weekly-results/:leagueId/week/:weekNumber" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <WeeklyResultsWrapper />
          </main>
        } />
        <Route path="/login" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <Login />
          </main>
        } />
        <Route path="/claim-account" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ClaimAccount />
          </main>
        } />
        <Route path="/reset-password" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ResetPassword />
          </main>
        } />
        <Route path="/password-setup" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><PasswordSetup /></ProtectedRoute>
          </main>
        } />
        <Route path="/simulator-booking" element={
          <main className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 py-0 sm:py-8">
            <BetaProtectedRoute>
              <BookingGuard>
                <BookingPage />
              </BookingGuard>
            </BetaProtectedRoute>
          </main>
        } />
        <Route path="/club-onboarding" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <BetaProtectedRoute>
              <OnboardingWorkflow />
            </BetaProtectedRoute>
          </main>
        } />
        <Route path="/booking-settings" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><BookingSettingsAdmin /></AdminProtectedRoute>
          </main>
        } />

        {/* Weekly Hole-in-One Challenge Routes */}
        <Route path="/challenges" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute>
              <ChallengesPageWrapper />
            </ProtectedRoute>
          </main>
        } />
        <Route path="/challenges/:challengeId" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ProtectedRoute><ChallengeLeaderboardWrapper /></ProtectedRoute>
          </main>
        } />
        <Route path="/challenges/admin" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><WeeklyChallengeAdmin /></AdminProtectedRoute>
          </main>
        } />

        {/* User-facing Signups */}
        <Route path="/signups/:id" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <SignupsProtectedRoute><SignupRegistration /></SignupsProtectedRoute>
          </main>
        } />
        <Route path="/signups" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <SignupsProtectedRoute><SignupList /></SignupsProtectedRoute>
          </main>
        } />

        {/* Signup Management */}
        <Route path="/admin/signups/:id" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><SignupDetails /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/signups" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><SignupManager /></AdminProtectedRoute>
          </main>
        } />
        <Route path="/admin/registration-templates" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <AdminProtectedRoute><RegistrationTemplateManager /></AdminProtectedRoute>
          </main>
        } />

        {/* Dev-only Component Showcase */}
        <Route path="/dev/components" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <ComponentShowcase />
          </main>
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
        <ToastContainer />
      </Router>
    </AuthProvider>
  );
}

export default App;
